import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import TicketSaleTargetDetails from 'Services/SalesManagement/Features/TicketSale/Components/TicketSaleTargetDetails'
import {
  useDeleteSingleTicketSaleTargetMutation,
  useGetSingleTicketSaleTargetQuery
} from 'Services/SalesManagement/Features/TicketSale/ticketSaleApi'
import { TicketSaleTargetTabType } from 'Services/SalesManagement/Features/TicketSale/types'
import { SALES_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const TICKET_SALE_TARGET_HAS_SALES_ERROR_CODE = 'ticket_sale_target_has_sales'

interface TicketSaleTargetDrawerProps {
  open: boolean
  handleClose: () => void
  selectedTicketSaleTargetId: string | undefined
}

export default function TicketSaleTargetDrawer({
  open,
  handleClose,
  selectedTicketSaleTargetId
}: Readonly<TicketSaleTargetDrawerProps>) {
  const navigate = useNavigate()
  const { confirm } = useConfirm()

  const baseUrl = `${SALES_MANAGEMENT_ROOT_URL}/ticket-sales/ticket-sale-targets`
  const { data: singleTicketSaleTarget, isFetching: isSingleTicketSaleTargetFetching } =
    useGetSingleTicketSaleTargetQuery(selectedTicketSaleTargetId ?? '', {
      skip: !selectedTicketSaleTargetId
    })
  const [deleteSingleTicketSaleTarget, { isLoading: isDeleteLoading }] =
    useDeleteSingleTicketSaleTargetMutation()

  const handleDelete = () => {
    if (!selectedTicketSaleTargetId) return

    deleteSingleTicketSaleTarget(selectedTicketSaleTargetId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Ticket sale target successfully deleted.`)
        handleClose()
      })
      .catch(error => {
        if (error?.data?.errorCode === TICKET_SALE_TARGET_HAS_SALES_ERROR_CODE) {
          SnackbarUtils.error(t`A ticket sale target with sales cannot be deleted.`)
          return
        }
        SnackbarUtils.error(t`There was an error deleting the ticket sale target.`)
      })
  }

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          <DeleteButton
            onClick={() =>
              confirm({
                title: t`Are you sure?`,
                message: t`Are you sure you want to delete the ticket sale target?`,
                handleConfirm: () => {
                  handleDelete()
                }
              })
            }
            loading={isDeleteLoading}
          >{t`Delete target`}</DeleteButton>
          <CustomButton
            onClick={() => navigate(`${baseUrl}/${selectedTicketSaleTargetId}/edit`)}
            size="small"
            startIcon={<EditIcon />}
          >{t`Edit`}</CustomButton>
        </Box>
      }
    >
      <WithLoader loading={isSingleTicketSaleTargetFetching}>
        {singleTicketSaleTarget && (
          <TabContext value={TicketSaleTargetTabType.DETAILS}>
            <TabList aria-label={t`Details`}>
              <Tab label={t`Details`} value={TicketSaleTargetTabType.DETAILS} />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={TicketSaleTargetTabType.DETAILS}>
              <TicketSaleTargetDetails ticketSaleTarget={singleTicketSaleTarget} />
            </TabPanel>
          </TabContext>
        )}
      </WithLoader>
    </DrawerRight>
  )
}
